/*
 * Enclosure Finder UI (story #68).
 *
 * Rules live in enclosure-finder-engine.js (window.EnclosureFinder). This file
 * only renders, reads live product data from the section's JSON, adds to the
 * cart, sends the "email me my results" event to Klaviyo and reports ef_ events
 * to GA4.
 *
 * Product data comes from the section's product pickers. A product missing from
 * the data (removed from the picker, deleted, unpublished) is simply left out,
 * never shown as a broken card.
 *
 * Cart: one /cart/add.js call with every item. No order-level attributes, ever
 * (they break the Cin7 sync).
 *
 * Answers are mirrored into the URL (?cats=2&loc=grass&l=4.5&w=2.5) so the
 * "email me my results" link reopens straight onto the result.
 *
 * Tracking (mirrors the DIY Cat Run Builder's bk_ events, same transport):
 *   ef_journey_start   page opened (entry: fresh | result_link)
 *   ef_step_view       every step shown, however it was reached (ef_step, step_index)
 *   ef_step_complete   an answer given (ef_step, step_index, answer [+ ef_length_m, ef_width_m])
 *   Params the builder also uses (step, length, width, value) carry an ef_ prefix so the
 *   builder's BK custom dimensions/metrics never count quiz traffic.
 *   ef_result / ef_nothing_fits   the recommendation
 *   ef_select          enclosure picked (enclosure, range, size, source, position)
 *   ef_back / ef_restart          navigation (from_step, to_step, via)
 *   ef_details_click   "View details" / "View full details" (handle, place)
 *   ef_add_to_cart / ef_cart_error / ef_email
 *   ef_return_from_cart  came back from the cart via the browser's Back (page restored from cache)
 * Every cart line also carries the hidden line property _finder_source so orders can be
 * traced (a line property, never an order attribute: those broke the Cin7/3PL sync).
 * A separate key from the builder's _kit_source on purpose: the builder's Shopify Flow
 * tags any order with _kit_source as "DIY Cat Run Builder".
 *
 * Debug: localStorage.setItem('ef_debug', '1') logs every analytics event.
 */
(function () {
  'use strict';

  var ICON = {
    check: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10.5l4 4 8-9"/></svg>',
    shield: '<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1l5.5 2v4.2c0 3.4-2.3 6.4-5.5 7.3-3.2-.9-5.5-3.9-5.5-7.3V3L8 1zm-1 9.4l4.2-4.2-1-1L7 8.4 5.8 7.2l-1 1L7 10.4z"/></svg>',
    heart: '<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 14s-5.5-3.4-5.5-7.2A3 3 0 0 1 8 4.9a3 3 0 0 1 5.5 1.9C13.5 10.6 8 14 8 14z"/></svg>',
    inside: '<svg viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M4 13L14 5l10 8v10H4z"/><path d="M11 23v-6h6v6"/></svg>',
    grass: '<svg viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M3 23h22"/><path d="M7 23c0-5 1-8 3-11M12 23c0-6-1-10-3-13M17 23c0-5 2-9 5-12M21 23c0-4-1-7-3-9"/></svg>',
    hard: '<svg viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="7" width="22" height="16" rx="1"/><path d="M3 15h22M11 7v8M17 15v8"/></svg>',
    travel: '<svg viewBox="0 0 28 28" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="9" width="22" height="13" rx="2"/><path d="M10 9V6h8v3"/><path d="M3 15h22"/></svg>'
  };

  var LOCATIONS = [
    { id: 'inside', label: 'Inside, or a covered patio or balcony' },
    { id: 'grass', label: 'Out in the backyard on grass' },
    { id: 'hard', label: 'On pavers, concrete or a deck' },
    { id: 'travel', label: "It'll travel with us", sub: 'Holidays, camping, moving house' }
  ];
  var CATS = [
    { id: 1, label: 'Just one' },
    { id: 2, label: 'Two' },
    { id: 3, label: 'Three or more' }
  ];

  var SHORT = {
    'premium-shade-sail-to-suit-all-freestanding-enclosures-square': 'Shade sail, 1.8m',
    'premium-shade-sail-to-suit-1-2m-enclosure': 'Shade sail, 1.2m',
    'uv50-waterproof-cover': 'UV50+ waterproof cover, 1.8m',
    'uv50-waterproof-cover-for-1-2m-cat-enclosures': 'UV50+ waterproof cover, 1.2m',
    'uv50-waterproof-cover-for-lite-2-3m-cat-enclosure': 'UV50+ waterproof cover, Lite 2.3m',
    'enclosure-flooring-for-lite-portable-enclosures': 'Lite flooring panel, 1.2m',
    'enclosure-flooring-for-premium-1-2m-freestanding-enclosure': 'Premium flooring, 1.2m',
    'enclosure-flooring-for-classic-freestanding-enclosures': 'Classic flooring panel, 1.8m',
    'enclosure-flooring-for-premium-freestanding-enclosures': 'Premium flooring panel, 1.8m',
    'artificial-cat-grass': 'Artificial cat grass, 1.5 x 3m roll',
    'artificial-cat-grass-1m-x-1m': 'Artificial cat grass, 1 x 1m',
    'heavy-duty-storage-bag': 'Heavy duty storage bag',
    'cat-enclosure-weight-bag': 'Weight bag',
    'cat-hammock-to-suit-1-2m-freestanding-enclosure': 'Hammock for Lite and 1.2m',
    'cat-hammock-to-suit-all-freestanding-enclosures': 'Extra hammock, 1.8m section',
    'skywalks-weatherproof-scratcher': 'Skywalks weatherproof scratcher',
    'skywalks-outdoor-cat-tree': 'Skywalks outdoor cat tree',
    'tunnel': 'Cat tunnel',
    'custom-engraved-pvc-name-plaque': 'Engraved name plaque'
  };

  var BUILD = {
    classic: 'One-piece net pulls over the frame and cable-ties at the base.',
    premium: 'Net hangs inside the steel frame on straps you can tighten, with a stitched edge along the ground. Tighter fit, extra peace of mind.',
    'trek-portable-cat-enclosure-1-78m': 'Lightweight PVC frame that slides together. Best in a covered or semi-covered spot.',
    'lite-portable-cat-enclosure-2-3m': 'Aluminium frame that slides together with no tools. Easy to move around.',
    'lite-tower-portable-cat-enclosure-2-3m-height': 'Aluminium frame, tall enough to stand in. Stand it against a wall or peg it down.'
  };

  // Weights where a Premium differs from its Classic twin (null = not published).
  var KG = {
    'premium-portable-cat-enclosure-1-2m': null,
    'premium-portable-cat-enclosure-double-3-6m': null,
    'premium-portable-cat-enclosure-triple-5-4m': 50
  };

  var STEPS = ['cats', 'location', 'space', 'result', 'accessories'];
  var LINE_SOURCE_KEY = '_finder_source';
  var LINE_SOURCE_VALUE = 'Enclosure Finder';
  var STEP_LABELS = { cats: 'Cats', location: 'Location', space: 'Space', result: 'Result', accessories: 'Accessories' };
  var TICK = '<svg class="ef-step-tick" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  // Where the header "Back" goes from each step. Answers, measurements and ticked accessories are kept.
  var PREV = { location: 'cats', space: 'location', result: 'space', accessories: 'result' };
  var KLAVIYO_REVISION = '2024-10-15';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    var n = cents / 100;
    var hasCents = Math.round(cents) % 100 !== 0;
    return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 });
  }
  function m(n) { return (Math.round(n * 100) / 100) + 'm'; }
  function debugOn() { try { return window.localStorage.getItem('ef_debug') === '1'; } catch (e) { return false; } }

  function init(root) {
    var E = window.EnclosureFinder;
    if (!E || root.getAttribute('data-ef-ready')) return null;
    root.setAttribute('data-ef-ready', '1');

    var cfg = {};
    try { cfg = JSON.parse(root.querySelector('[data-ef-config]').textContent); } catch (e) { cfg = {}; }
    var PRODUCTS = {};
    try {
      var raw = JSON.parse(root.querySelector('[data-ef-products]').textContent);
      (raw.enclosures || []).concat(raw.accessories || []).forEach(function (p) { if (p && p.handle && p.variant_id) PRODUCTS[p.handle] = p; });
    } catch (e) { /* no data: every product is treated as missing */ }

    var app = root.querySelector('[data-ef-app]');
    var stepsEl = root.querySelector('[data-ef-steps]');
    var utilEl = root.querySelector('[data-ef-util]');
    var state = fresh();

    function fresh() {
      return { step: 'cats', cats: null, location: null, length: '', width: '', result: null, chosen: null, acc: {}, email: null, busy: false, error: null };
    }
    function has(h) { return !!PRODUCTS[h]; }
    function range(h) { return E.RANGE[h]; }
    function rangeLabel(h) { return { classic: 'Classic', premium: 'Premium', lite: 'Lite range' }[range(h)]; }

    // ---------- analytics (same transport as the buying journey) ----------
    var gaReady = false;
    function gtag() { window.dataLayer = window.dataLayer || []; window.dataLayer.push(arguments); }
    function startGa() {
      if (gaReady || !cfg.ga4 || !document.head) return;
      gaReady = true;
      try {
        var sc = document.createElement('script');
        sc.async = true;
        sc.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(cfg.ga4);
        document.head.appendChild(sc);
        gtag('js', new Date());
        var conf = { send_page_view: false };
        var mm = document.cookie.match(/_ga=GA\d+\.\d+\.([0-9]+\.[0-9]+)/);
        if (mm) conf.client_id = mm[1];
        if (debugOn()) conf.debug_mode = true;
        gtag('config', cfg.ga4, conf);
      } catch (e) { /* never let tracking break the quiz */ }
    }
    // Consent gate, the same rules as assets/b-track.js (#146): GA and the dataLayer only while the
    // shopper allows analytics; before Shopify's consent API loads, events wait for it (loaded the way
    // Shopify documents); denied or no API, nothing is sent. b-track.js itself isn't called: it is an
    // ES module and adds a `store` param, which would change the ef_ payloads.
    function consentReady() { var cp = window.Shopify && window.Shopify.customerPrivacy; return !!(cp && typeof cp.analyticsProcessingAllowed === 'function'); }
    function consentGiven() { try { return consentReady() && window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true; } catch (e) { return false; } }
    // Dedup for GA is kept apart from the publish dedup and only updated on a permitted send, so an
    // event dropped for consent still goes out if the shopper allows analytics and it happens again.
    var lastGa = {};
    function sendGa(ev, p) {
      if (!consentGiven()) return;
      var key = JSON.stringify(p);
      if (lastGa[ev] === key && ev !== 'ef_add_to_cart') return;
      lastGa[ev] = key;
      window.dataLayer = window.dataLayer || [];
      var row = { event: ev };
      for (var k in p) row[k] = p[k];
      window.dataLayer.push(row);
      if (cfg.ga4) { startGa(); gtag('event', ev, p); }
    }
    var held = null;
    function flushHeld() { var q = held || []; held = null; q.forEach(function (x) { sendGa(x[0], x[1]); }); }
    function queueGa(ev, p) {
      if (consentReady()) return sendGa(ev, p);
      if (!held) {
        held = [];
        var loader = window.Shopify && window.Shopify.loadFeatures;
        if (typeof loader === 'function') {
          try { loader.call(window.Shopify, [{ name: 'consent-tracking-api', version: '0.1' }], flushHeld); } catch (e) { setTimeout(flushHeld, 0); }
        } else setTimeout(flushHeld, 0);
      }
      if (held.length < 50) held.push([ev, p]);
    }
    var lastSent = {};
    function track(name, params) {
      try {
        var ev = 'ef_' + name, p = params || {};
        var key = JSON.stringify(p);
        queueGa(ev, p);
        if (lastSent[ev] === key && name !== 'add_to_cart') return;
        lastSent[ev] = key;
        // Mirror to Shopify customer events so a Custom Pixel can subscribe if ever wanted. Not
        // consent-gated, as in assets/b-track.js: Shopify applies the shopper's consent before any pixel sees it.
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish(ev, p);
        }
        if (debugOn() && window.console) console.log('[ef analytics]', ev, p);
      } catch (e) { /* never let tracking break the quiz */ }
    }

    // ---------- URL state ----------
    function answersQuery() {
      return 'cats=' + state.cats + '&loc=' + encodeURIComponent(state.location) + '&l=' + encodeURIComponent(state.length) + '&w=' + encodeURIComponent(state.width);
    }
    function resultUrl() {
      var base = (cfg.pageUrl && /^https?:/.test(cfg.pageUrl)) ? cfg.pageUrl : (window.location.origin + window.location.pathname);
      return base + '?' + answersQuery();
    }
    function syncUrl() {
      try {
        var q = state.result ? '?' + answersQuery() : '';
        window.history.replaceState(null, '', window.location.pathname + q);
      } catch (e) { /* ignore */ }
    }
    function readUrl() {
      try {
        var p = new URLSearchParams(window.location.search);
        var cats = Number(p.get('cats')), loc = p.get('loc'), l = p.get('l'), w = p.get('w');
        var locOk = LOCATIONS.some(function (x) { return x.id === loc; });
        if ((cats === 1 || cats === 2 || cats === 3) && locOk && parseFloat(l) > 0 && parseFloat(w) > 0) {
          state.cats = cats; state.location = loc; state.length = String(parseFloat(l)); state.width = String(parseFloat(w));
          return true;
        }
      } catch (e) { /* ignore */ }
      return false;
    }

    // ---------- rendering ----------
    // Step bar like the DIY Cat Run Builder: done steps tick and jump back when clicked,
    // the current step is ringed, later steps are locked (answers may change on the way back).
    function renderSteps() {
      var i = STEPS.indexOf(state.step);
      var html = '';
      for (var k = 0; k < STEPS.length; k++) {
        var status = k < i ? 'complete' : k === i ? 'current' : 'locked';
        var id = STEPS[k];
        html += '<button type="button" class="ef-step" data-status="' + status + '"' +
          (status === 'complete' && !state.busy ? ' data-ef-action="goto" data-v="' + id + '"' : ' aria-disabled="true"') +
          (status === 'current' ? ' aria-current="step"' : '') + '>' +
          '<span class="ef-step-marker" aria-hidden="true"><span class="ef-step-number">' + (k + 1) + '</span>' + TICK + '</span>' +
          '<span class="ef-step-label">' + STEP_LABELS[id] + '</span></button>';
      }
      stepsEl.innerHTML = html;
      // Back one step and Start again are always one click away once they're past question 1.
      var util = '';
      if (PREV[state.step]) util += '<button type="button" class="ef-linkbtn" data-ef-action="goto" data-v="' + PREV[state.step] + '"' + (state.busy ? ' disabled' : '') + '>&larr; Back</button>';
      if (i > 0) util += '<button type="button" class="ef-linkbtn" data-ef-action="restart"' + (state.busy ? ' disabled' : '') + '>Start again</button>';
      // Guarded: during a deploy the cached section can briefly lag the new script,
      // and a missing row must never blank the whole quiz.
      if (utilEl) { utilEl.innerHTML = util; utilEl.hidden = !util; }
    }

    function plan(len, wid, sizeKey) {
      var W = 360, H = 220, pad = 34;
      var L = num(len), D = num(wid);
      if (!(L > 0) || !(D > 0)) {
        return '<svg viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="Enter your space to see it drawn to scale"><rect x="' + pad + '" y="' + pad + '" width="' + (W - pad * 2) + '" height="' + (H - pad * 2) + '" fill="none" stroke="var(--ef-plan-space)" stroke-width="2" stroke-dasharray="6 5" rx="4"/><text x="' + W / 2 + '" y="' + H / 2 + '" text-anchor="middle" fill="var(--ef-muted)" font-size="13">Your space, drawn to scale</text></svg>';
      }
      var sc = Math.min((W - pad * 2) / L, (H - pad * 2) / D);
      var sw = L * sc, sh = D * sc, x0 = (W - sw) / 2, y0 = (H - sh) / 2;
      var out = '<svg viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="Your ' + m(L) + ' by ' + m(D) + ' space' + (sizeKey ? ' with the enclosure footprint' : '') + '">';
      out += '<rect x="' + x0 + '" y="' + y0 + '" width="' + sw + '" height="' + sh + '" fill="var(--ef-surface-2)" stroke="var(--ef-plan-space)" stroke-width="2" stroke-dasharray="6 5" rx="3"/>';
      out += '<text x="' + (x0 + sw / 2) + '" y="' + (y0 - 10) + '" text-anchor="middle" fill="var(--ef-muted)" font-size="13" font-weight="700">' + m(L) + '</text>';
      out += '<text x="' + (x0 - 10) + '" y="' + (y0 + sh / 2) + '" text-anchor="middle" fill="var(--ef-muted)" font-size="13" font-weight="700" transform="rotate(-90 ' + (x0 - 10) + ' ' + (y0 + sh / 2) + ')">' + m(D) + '</text>';
      if (sizeKey) {
        var z = E.SIZES[sizeKey];
        var eL = z.L, eW = z.W;
        if (!(eL <= L + 1e-9 && eW <= D + 1e-9)) { eL = z.W; eW = z.L; }
        var ew = eL * sc, eh = eW * sc;
        var ex = x0 + Math.min(0.3 * sc, (sw - ew) / 2), ey = y0 + Math.min(0.3 * sc, (sh - eh) / 2);
        out += '<rect x="' + ex + '" y="' + ey + '" width="' + ew + '" height="' + eh + '" fill="var(--ef-plan-fill)" stroke="var(--ef-accent)" stroke-width="2" rx="2"/>';
        if (ew > 96 && eh > 28) out += '<text x="' + (ex + ew / 2) + '" y="' + (ey + eh - 8) + '" text-anchor="middle" fill="var(--ef-accent)" font-size="13" font-weight="900">' + m(eL) + ' x ' + m(eW) + '</text>';
      }
      return out + '</svg>';
    }

    function viewCats() {
      return '<section class="ef-q"><div class="ef-q-head"><h2 class="ef-title">How many cats will be using the Enclosure?</h2><p class="ef-lede">We\'ll size it so everyone has room to roam.</p></div>' +
        '<div class="ef-options">' + CATS.map(function (c) {
          return '<button type="button" class="ef-opt" data-ef-action="cats" data-v="' + c.id + '" aria-pressed="' + (state.cats === c.id) + '"><span class="ef-opt-big">' + c.label + '</span></button>';
        }).join('') + '</div></section>';
    }
    function viewLocation() {
      return '<section class="ef-q"><div class="ef-q-head"><h2 class="ef-title">Where will it go?</h2><p class="ef-lede">This tells us which frames and accessories suit your spot.</p></div>' +
        '<div class="ef-options">' + LOCATIONS.map(function (l) {
          return '<button type="button" class="ef-opt" data-ef-action="location" data-v="' + l.id + '" aria-pressed="' + (state.location === l.id) + '">' + ICON[l.id] + '<span class="ef-opt-big">' + esc(l.label) + '</span>' + (l.sub ? '<span class="ef-opt-small">' + esc(l.sub) + '</span>' : '') + '</button>';
        }).join('') + '</div><div class="ef-nav"><button type="button" class="ef-back" data-ef-action="goto" data-v="cats">Back</button></div></section>';
    }
    // A typed size as a number: "3m", " 3 " and "2,5" all count (Lee, 2026-11-05). Anything else ("3cm", "3.1.2") is
    // not a size, so it reads as NaN and keeps Next off rather than taking the number at its start.
    function num(v) {
      var m = /^\s*(\d+(?:[.,]\d+)?|[.,]\d+)\s*m?\s*$/i.exec(v == null ? '' : String(v));
      return m ? parseFloat(m[1].replace(',', '.')) : NaN;
    }
    function validDims() {
      var L = num(state.length), W = num(state.width);
      return L >= 0.3 && L <= 50 && W >= 0.3 && W <= 50;
    }
    // Text fields, not type=number: Safari and Firefox blank a number field's value for "3m" or "2,5" while still showing it.
    function viewSpace() {
      return '<section class="ef-q"><div class="ef-q-head"><h2 class="ef-title">How much space do you have?</h2>' +
        '<p class="ef-lede">Grab a tape measure, or pace it out (one big step is about 1m). A good guess is fine.</p></div>' +
        '<div class="ef-space"><div class="ef-q">' +
          '<div class="ef-dims">' +
            '<div class="ef-field"><label for="ef-len">Length</label><div class="ef-input"><input id="ef-len" inputmode="decimal" type="text" autocomplete="off" placeholder="0.0" value="' + esc(state.length) + '" data-ef-field="length"><span>m</span></div></div>' +
            '<div class="ef-field"><label for="ef-wid">Width</label><div class="ef-input"><input id="ef-wid" inputmode="decimal" type="text" autocomplete="off" placeholder="0.0" value="' + esc(state.width) + '" data-ef-field="width"><span>m</span></div></div>' +
          '</div>' +
          '<div class="ef-guesses"><span class="ef-hint">Not sure? Pick the closest:</span>' +
            E.QUICK_GUESSES.map(function (g) { return '<button type="button" class="ef-guess" data-ef-action="guess" data-v="' + g.id + '"><span>' + esc(g.label) + '</span><span class="ef-num">about ' + g.L + ' x ' + g.W + 'm</span></button>'; }).join('') +
          '</div>' +
        '</div>' +
        '<div class="ef-planbox"><div data-ef-plan>' + plan(state.length, state.width) + '</div><span class="ef-cap">Leave about 30cm around the edges so you can reach the door.</span></div></div>' +
        '<div class="ef-nav"><button type="button" class="ef-btn" data-ef-action="submit" data-ef-go' + (validDims() ? '' : ' disabled') + '>See my enclosure</button><button type="button" class="ef-back" data-ef-action="goto" data-v="location">Back</button></div></section>';
    }

    // Review stars from the product's reviews.rating (#146): five stars filled to the score.
    function stars(p) {
      var max = Number(p.rating_scale) || 5, score = Number(p.rating), n = Number(p.rating_count) || 0;
      if (!(score > 0) || !n) return '';
      var pct = Math.max(0, Math.min(100, score / max * 100));
      var row = '★★★★★';
      var label = 'Rated ' + (Math.round(score * 100) / 100) + ' out of ' + max + ', ' + n + (n === 1 ? ' review' : ' reviews');
      return '<span class="ef-stars" role="img" aria-label="' + esc(label) + '"><span class="ef-stars-row" aria-hidden="true">' + row +
        '<span class="ef-stars-fill" style="width:' + pct + '%">' + row + '</span></span><span class="ef-num" aria-hidden="true">' + (Math.round(score * 100) / 100) + ' (' + n + ')</span></span>';
    }

    function card(h, pair) {
      var p = PRODUCTS[h], r = range(h), z = E.SIZES[E.sizeKeyOf(h)];
      var kg = Object.prototype.hasOwnProperty.call(KG, h) ? KG[h] : z.kg;
      // The product's own filters.* fields when set (#146), the engine's size otherwise.
      var dims = (p.length && p.width && p.height ? 'L ' + p.length + ' x W ' + p.width + ' x H ' + p.height : 'L ' + z.L + 'm x W ' + z.W + 'm x H ' + z.H + 'm') + (kg ? ' · ' + kg + 'kg' : '');
      var buildLine = r === 'lite' ? BUILD[h] : BUILD[r];
      var badges = (p.badges || []).filter(function (b) { return b && b.label; });
      var hammockBadge = badges.some(function (b) { return /hammock/i.test(b.label); });
      // In a Classic/Premium pair, Premium is "Our pick": pink border, label, solid button.
      // Classic gets the outline button. Never "Most popular": Classic outsells it on units.
      var pick = pair && r === 'premium';
      return '<article class="ef-card' + (pick ? ' is-pick' : '') + '">' + (p.image ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="400">' : '') +
        '<div class="ef-card-body">' +
          '<div class="ef-card-top"><span class="ef-chips"><span class="ef-chip is-' + r + '">' + rangeLabel(h) + '</span>' + (pick ? '<span class="ef-ribbon">Our pick</span>' : '') + '</span>' + stars(p) + '</div>' +
          '<h3>' + esc(p.title) + '</h3>' +
          (badges.length ? '<span class="ef-badges">' + badges.map(function (b) {
            return '<span class="ef-badge"' + (b.colour ? ' style="background:' + esc(b.colour) + '"' : '') + '>' + esc(b.label) + '</span>';
          }).join('') + '</span>' : '') +
          '<ul class="ef-facts">' +
            (p.cats ? '<li><b>Purrfect for ' + esc(p.cats) + (p.cats === '1' ? ' Cat' : ' Cats') + '</b></li>' : '') +
            '<li class="ef-num">' + esc(dims) + '</li>' +
          '</ul>' +
          '<p class="ef-build">' + esc(buildLine) + '</p>' +
          (r === 'classic' || r === 'premium' ? '<span class="ef-perk is-steel">' + ICON.shield + 'Stainless steel reinforced netting</span>' : '') +
          (E.freeHammock(h) && !hammockBadge ? '<span class="ef-perk">' + ICON.heart + 'Free hammock included</span>' : '') +
          '<div class="ef-buy"><span class="ef-price">' + money(p.price) + '</span>' +
          (p.available ? '<button type="button" class="ef-btn' + (pair && !pick ? ' is-ghost' : '') + '" data-ef-action="choose" data-v="' + esc(h) + '">' + (pair ? 'Choose ' + rangeLabel(h) : 'Choose this one') + '</button>'
                       : '<p class="ef-note">Out of stock right now.</p>') + '</div>' +
          (p.available ? '<span class="ef-buy-next">Next: add extras if you like, then add to cart.</span>' : '') +
          '<a class="ef-view" href="' + esc(p.url) + '" target="_blank" rel="noopener" data-ef-details="' + esc(h) + '">View full details</a>' +
        '</div></article>';
    }

    function viewResult() {
      var r = state.result;
      if (r.status === 'nothing-fits') {
        return '<section class="ef-q"><div class="ef-q-head"><h2 class="ef-title">None of our enclosures fit a ' + m(r.length) + ' x ' + m(r.width) + ' space</h2>' +
          '<p class="ef-lede">The smallest one that suits your spot needs ' + r.smallest.L + ' x ' + r.smallest.W + 'm. For a tight balcony, cat netting across the opening usually works better.</p></div>' +
          '<div class="ef-planbox" style="max-width:420px">' + plan(state.length, state.width) + '</div>' +
          '<div class="ef-nav">' + (cfg.nettingUrl ? '<a class="ef-btn" href="' + esc(cfg.nettingUrl) + '">Shop cat netting</a>' : '') + '<button type="button" class="ef-btn is-ghost" data-ef-action="goto" data-v="space">Change my measurements</button></div></section>';
      }
      var products = r.products.filter(has);
      var pair = products.length > 1;
      var html = '<div class="ef-result-head"><div class="ef-q"><div class="ef-q-head"><h2 class="ef-title">A ' + esc(r.size.label) + ' enclosure suits you</h2></div>' +
        '<ul class="ef-reasons">' + r.reasons.map(function (x) { return '<li>' + ICON.check + '<span>' + esc(x) + '</span></li>'; }).join('') + '</ul>' +
        (r.note ? '<p class="ef-note">' + esc(r.note) + '</p>' : '') +
        (pair ? '<p class="ef-lede">It comes in two ranges. Same size, different net fitting.</p>' : '') +
        '</div><div class="ef-planbox">' + plan(state.length, state.width, r.sizeKey) + '<span class="ef-cap">Your space (dashed) with the enclosure footprint, to scale.</span></div></div>';
      html += products.length
        ? '<div class="ef-cards' + (pair ? '' : ' is-single') + '">' + products.map(function (h) { return card(h, pair); }).join('') + '</div>'
        : '<p class="ef-note">This one is not available online right now. Get in touch and we will help you find the right fit.</p>';
      var also = r.alsoFits ? r.alsoFits.products.filter(has) : [];
      if (also.length) {
        html += '<section class="ef-section"><h2>Also fits your space</h2><div class="ef-mini">' + also.map(function (h) {
          var p = PRODUCTS[h];
          return '<button type="button" class="ef-minicard" data-ef-action="choose" data-v="' + esc(h) + '"' + (p.available ? '' : ' disabled') + '>' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '<span><span class="ef-minicard-title">' + esc(p.title) + '</span><br><span class="ef-num">' + money(p.price) + '</span> · <span class="ef-chip is-' + range(h) + '">' + rangeLabel(h) + '</span></span></button>';
        }).join('') + '</div></section>';
      }
      html += viewEmail();
      html += '<div class="ef-nav" style="margin-top:28px"><button type="button" class="ef-back" data-ef-action="restart">Start again</button>' +
        (cfg.contactUrl ? '<a href="' + esc(cfg.contactUrl) + '">Not quite right? Talk to us</a>' : '') + '</div>';
      return html;
    }

    function viewEmail() {
      // Off until the Klaviyo flow exists (section setting "Show Email me my results").
      if (!cfg.emailEnabled || !cfg.klaviyoKey) return '';
      if (state.email && state.email.sent) {
        return '<section class="ef-section"><div class="ef-email"><h2>Sent to ' + esc(state.email.address) + '</h2><p class="ef-hint">Check your inbox in a few minutes. It has your size, both options and a link back to this result.</p></div></section>';
      }
      return '<section class="ef-section"><div class="ef-email"><h2>Want a copy?</h2><p class="ef-hint">We\'ll email your size and both options, so you can come back to it.</p>' +
        '<form data-ef-emailform novalidate><input type="email" id="ef-email" placeholder="you@example.com" autocomplete="email" required aria-label="Email address" value="' + esc(state.email ? state.email.address : '') + '"><button class="ef-btn" type="submit"' + (state.busy ? ' disabled' : '') + '>' + (state.busy ? 'Sending...' : 'Email me my results') + '</button></form>' +
        '<label class="ef-check"><input type="checkbox" id="ef-optin"' + (state.email && state.email.optIn ? ' checked' : '') + '> Also send me Catnets tips and offers.</label>' +
        (state.error ? '<span class="ef-error" role="alert">' + esc(state.error) + '</span>' : '') + '</div></section>';
    }

    // ---------- accessories ----------
    function buildAcc() {
      var list = E.accessoriesFor(state.chosen, state.location).filter(function (a) { return has(a.handle) && PRODUCTS[a.handle].available; });
      var prev = state.acc;
      state.acc = {};
      list.forEach(function (a) {
        var old = prev[a.handle];
        state.acc[a.handle] = { item: a, on: old ? old.on : false, qty: old ? Math.min(old.qty, a.max) : a.qty };
      });
    }
    function selectedAcc() {
      return Object.keys(state.acc).map(function (h) { return state.acc[h]; }).filter(function (x) { return x.on; })
        .map(function (x) { return { handle: x.item.handle, qty: x.qty }; });
    }
    function totals() {
      var sel = selectedAcc();
      var t = PRODUCTS[state.chosen].price;
      sel.forEach(function (a) { t += PRODUCTS[a.handle].price * a.qty; });
      return { total: t, count: sel.length };
    }
    // One tile per accessory. Tiles sit in a row per group (see .ef-acc-grid).
    function accRow(a) {
      var s = state.acc[a.handle], p = PRODUCTS[a.handle];
      var id = 'ef-acc-' + a.handle;
      var name = SHORT[a.handle] || p.title;
      return '<div class="ef-acc' + (s.on ? ' is-on' : '') + (a.highlight ? ' is-highlight' : '') + '">' +
        '<label class="ef-acc-pick" for="' + esc(id) + '">' +
          '<input type="checkbox" id="' + esc(id) + '" data-ef-action="toggle" data-v="' + esc(a.handle) + '"' + (s.on ? ' checked' : '') + '>' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '<span class="ef-acc-noimg"></span>') +
        '</label>' +
        '<label for="' + esc(id) + '" class="ef-acc-label">' + (a.highlight ? '<span class="ef-ribbon">Highly recommended</span>' : '') + '<span class="ef-acc-name">' + esc(name) + '</span>' +
          (a.why ? '<span class="ef-acc-why">' + esc(a.why) + '</span>' : '') + '</label>' +
        // New tab on purpose: ticked accessories aren't saved if they leave the page.
        '<a class="ef-acc-link" href="' + esc(p.url) + '" target="_blank" rel="noopener" data-ef-details="' + esc(a.handle) + '">View details<span class="ef-sr"> of ' + esc(name) + ' (opens in a new tab)</span></a>' +
        '<div class="ef-acc-foot"><span class="ef-acc-price ef-num"><b>' + money(p.price * s.qty) + '</b>' + (s.qty > 1 ? '<span class="ef-acc-why">' + money(p.price) + ' each</span>' : '') + '</span>' +
          '<span class="ef-qty"><button type="button" data-ef-action="qty" data-v="' + esc(a.handle) + '" data-d="-1" aria-label="One less ' + esc(name) + '"' + (s.qty <= 1 ? ' disabled' : '') + '>&minus;</button><span>' + s.qty + '</span><button type="button" data-ef-action="qty" data-v="' + esc(a.handle) + '" data-d="1" aria-label="One more ' + esc(name) + '"' + (s.qty >= a.max ? ' disabled' : '') + '>+</button></span></div>' +
      '</div>';
    }
    function viewAccessories() {
      var h = state.chosen, p = PRODUCTS[h];
      var items = Object.keys(state.acc).map(function (k) { return state.acc[k].item; });
      var rec = items.filter(function (a) { return a.recommended; });
      var html = '<div class="ef-chosen">' + (p.image ? '<img src="' + esc(p.image) + '" alt="">' : '') + '<div class="ef-chosen-body"><span class="ef-chip is-' + range(h) + '">' + rangeLabel(h) + '</span><h3>' + esc(p.title) + '</h3><span class="ef-num">' + money(p.price) + (E.freeHammock(h) ? ' · free hammock included' : '') + '</span></div><button type="button" class="ef-linkbtn" data-ef-action="goto" data-v="result">Change</button></div>' +
        '<div class="ef-q-head"><h2 class="ef-title">Kit out your enclosure</h2><p class="ef-lede">Everything here fits the ' + esc(p.title) + ', with the quantity it needs already set. Tick what you want.</p></div>';
      if (rec.length) html += '<div class="ef-group" data-ef-group="recommended"><h3>Recommended for your space</h3><div class="ef-acc-grid">' + rec.map(accRow).join('') + '</div></div>';
      // Small groups share a line (--n = tiles in the group), so one-item groups don't each take a full row.
      html += '<div class="ef-groups">';
      E.GROUPS.forEach(function (g) {
        var rows = items.filter(function (a) { return a.group === g.id && !a.recommended; });
        if (rows.length) html += '<div class="ef-group" data-ef-group="' + g.id + '" style="--n:' + rows.length + '"><h3>' + esc(g.label) + '</h3><div class="ef-acc-grid">' + rows.map(accRow).join('') + '</div></div>';
      });
      html += '</div>';
      var t = totals();
      html += '<div class="ef-bar"><div><div class="ef-total">' + money(t.total) + '</div><div class="ef-sub">Enclosure' + (t.count ? ' + ' + t.count + ' accessor' + (t.count === 1 ? 'y' : 'ies') : '') + '</div></div>' +
        '<div class="ef-nav">' + (state.error ? '<span class="ef-error" role="alert">' + esc(state.error) + '</span>' : '') +
        '<button type="button" class="ef-linkbtn" data-ef-action="cart" data-v="skip"' + (state.busy ? ' disabled' : '') + '>Skip, just the enclosure</button>' +
        '<button type="button" class="ef-btn" data-ef-action="cart"' + (state.busy ? ' disabled' : '') + '>' + (state.busy ? 'Adding...' : (t.count ? 'Add enclosure + ' + t.count + ' to cart' : 'Add enclosure to cart')) + '</button></div></div>';
      return html;
    }

    function render() {
      renderSteps();
      var v = { cats: viewCats, location: viewLocation, space: viewSpace, result: viewResult, accessories: viewAccessories }[state.step];
      app.innerHTML = v();
    }
    function rerenderInPlace(focusId) {
      var y = window.scrollY; render();
      try { window.scrollTo(0, y); } catch (e) { /* jsdom */ }
      if (focusId) { var el = document.getElementById(focusId); if (el) el.focus({ preventScroll: true }); }
    }
    function stepIndex(id) { return STEPS.indexOf(id) + 1; }
    function trackView() { track('step_view', { ef_step: state.step, step_index: stepIndex(state.step) }); }
    function complete(stepName, params) {
      var p = { ef_step: stepName, step_index: stepIndex(stepName) };
      for (var k in params) p[k] = params[k];
      track('step_complete', p);
    }

    // quiet = first render from a results link: no scrolling, no focus move.
    function go(step, quiet) {
      state.step = step; state.error = null;
      render();
      trackView();
      if (quiet) return;
      try { root.scrollIntoView({ block: 'start' }); } catch (e) { /* jsdom */ }
      var h = app.querySelector('.ef-title');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
    }

    function completeMeasured() {
      state.length = String(num(state.length)); state.width = String(num(state.width));
      complete('space', { answer: 'measured', ef_length_m: parseFloat(state.length), ef_width_m: parseFloat(state.width) });
    }

    function submit(quiet) {
      state.result = E.recommend({ cats: state.cats, location: state.location, length: parseFloat(state.length), width: parseFloat(state.width) });
      state.email = null;
      syncUrl();
      if (state.result.status === 'ok') {
        track('result', { ef_step: 'result', step_index: stepIndex('result'), size: state.result.sizeKey, products: state.result.products.join('|'), cats: state.cats === 3 ? '3+' : String(state.cats), location: state.location, ef_length_m: parseFloat(state.length), ef_width_m: parseFloat(state.width), note: state.result.note ? 'undersized' : 'none' });
      } else if (state.result.status === 'nothing-fits') {
        track('nothing_fits', { location: state.location, ef_length_m: parseFloat(state.length), ef_width_m: parseFloat(state.width) });
      }
      go('result', quiet);
    }

    function addToCart(withAcc) {
      var props = {}; props[LINE_SOURCE_KEY] = LINE_SOURCE_VALUE;
      var items = [{ id: PRODUCTS[state.chosen].variant_id, quantity: 1, properties: props }];
      var sel = withAcc ? selectedAcc() : [];
      sel.forEach(function (a) { items.push({ id: PRODUCTS[a.handle].variant_id, quantity: a.qty, properties: props }); });
      var value = PRODUCTS[state.chosen].price;
      sel.forEach(function (a) { value += PRODUCTS[a.handle].price * a.qty; });
      state.busy = true; state.error = null; rerenderInPlace();
      track('add_to_cart', { enclosure: state.chosen, range: range(state.chosen), accessories: sel.map(function (a) { return a.handle + 'x' + a.qty; }).join('|').slice(0, 95), accessory_count: sel.length, with_accessories: sel.length ? 'yes' : 'no', ef_cart_value: value / 100 });
      return fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        // Shopify answers errors as JSON, but a rate limit or outage can return an HTML page:
        // never show the customer a JSON parse error.
        if (!res.ok) return res.json().catch(function () { return {}; }).then(function (j) {
          var err = new Error(j && (j.description || j.message) || 'We could not add that to your cart');
          err.status = res.status;
          throw err;
        });
        window.location.href = cfg.cartUrl || '/cart';
      }).catch(function (err) {
        state.busy = false;
        track('cart_error', { enclosure: state.chosen, status: (err && err.status) || 0, message: String(err && err.message || 'unknown').slice(0, 95) });
        state.error = (err && err.message ? err.message + '. ' : '') + 'Please try again, or add the items from their product pages.';
        rerenderInPlace();
      });
    }

    function klaviyo(path, body) {
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(cfg.klaviyoKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify(body)
      }).then(function (res) { if (!res.ok) throw new Error('Klaviyo ' + res.status); });
    }
    function sendEmail(address, optIn) {
      var r = state.result;
      var products = r.products.filter(has).map(function (h) {
        var p = PRODUCTS[h];
        return { handle: h, title: p.title, range: rangeLabel(h), price: p.price / 100, price_label: money(p.price), url: window.location.origin + p.url, image: p.image && p.image.indexOf('//') === 0 ? 'https:' + p.image : p.image, free_hammock: E.freeHammock(h) };
      });
      var event = {
        data: { type: 'event', attributes: {
          properties: {
            cats: state.cats === 3 ? '3+' : String(state.cats),
            location: state.location,
            length_m: parseFloat(state.length),
            width_m: parseFloat(state.width),
            recommended_size: r.size.label,
            reasons: r.reasons,
            note: r.note || '',
            products: products,
            accessories: selectedAcc().map(function (a) { return { handle: a.handle, title: SHORT[a.handle] || PRODUCTS[a.handle].title, qty: a.qty, price: PRODUCTS[a.handle].price / 100, price_label: money(PRODUCTS[a.handle].price) }; }),
            result_url: resultUrl()
          },
          metric: { data: { type: 'metric', attributes: { name: 'Enclosure Finder Result' } } },
          profile: { data: { type: 'profile', attributes: { email: address } } }
        } }
      };
      var calls = [klaviyo('events', event)];
      if (optIn && cfg.klaviyoList) {
        calls.push(klaviyo('subscriptions', {
          data: { type: 'subscription', attributes: {
            custom_source: 'Enclosure Finder',
            profile: { data: { type: 'profile', attributes: { email: address, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
          }, relationships: { list: { data: { type: 'list', id: cfg.klaviyoList } } } }
        }));
      }
      return Promise.all(calls);
    }

    // ---------- events ----------
    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-ef-action]');
      if (!t || t.tagName === 'INPUT' || t.disabled) return;
      var a = t.getAttribute('data-ef-action'), v = t.getAttribute('data-v');
      if (a === 'cats') {
        state.cats = Number(v); complete('cats', { answer: v === '3' ? '3+' : v }); go('location');
      }
      else if (a === 'location') { state.location = v; complete('location', { answer: v }); go('space'); }
      else if (a === 'goto') {
        var via = t.classList.contains('ef-step') ? 'step_bar' : t.closest('[data-ef-util]') ? 'back_link' : 'in_page';
        track('back', { from_step: state.step, to_step: v, via: via });
        go(v);
      }
      else if (a === 'guess') {
        var g = E.QUICK_GUESSES.filter(function (x) { return x.id === v; })[0];
        state.length = String(g.L); state.width = String(g.W);
        complete('space', { answer: 'guess_' + g.id, ef_length_m: g.L, ef_width_m: g.W }); submit();
      }
      else if (a === 'submit') { if (validDims()) { completeMeasured(); submit(); } }
      else if (a === 'choose') {
        if (!has(v)) return;
        if (state.chosen !== v) state.acc = {};
        state.chosen = v; buildAcc();
        var card = t.closest('.ef-card');
        var cards = card ? Array.prototype.slice.call(app.querySelectorAll('.ef-card')) : [];
        track('select', {
          enclosure: v, range: range(v), size: E.sizeKeyOf(v),
          source: t.classList.contains('ef-minicard') ? 'also_fits' : cards.length > 1 ? 'pair' : 'single',
          position: cards.length > 1 ? (cards.indexOf(card) === 0 ? 'left' : 'right') : 'only'
        });
        go('accessories');
      }
      else if (a === 'qty') {
        var s = state.acc[v]; s.qty = Math.max(1, Math.min(s.item.max, s.qty + Number(t.getAttribute('data-d')))); s.on = true;
        rerenderInPlace();
      }
      else if (a === 'cart') { if (!state.busy) addToCart(v !== 'skip'); }
      else if (a === 'restart') { track('restart', { from_step: state.step }); state = fresh(); syncUrl(); go('cats'); }
    });
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.getAttribute('data-ef-action') === 'toggle') {
        state.acc[t.getAttribute('data-v')].on = t.checked;
        rerenderInPlace(t.id);
      }
    });
    root.addEventListener('input', function (e) {
      var f = e.target.getAttribute('data-ef-field');
      if (!f) return;
      state[f] = e.target.value;
      root.querySelector('[data-ef-plan]').innerHTML = plan(state.length, state.width);
      root.querySelector('[data-ef-go]').disabled = !validDims();
    });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.getAttribute && e.target.getAttribute('data-ef-field') && validDims()) {
        e.preventDefault(); completeMeasured(); submit();
      }
    });
    root.addEventListener('submit', function (e) {
      if (!e.target.hasAttribute('data-ef-emailform')) return;
      e.preventDefault();
      if (state.busy) return;
      var address = root.querySelector('#ef-email').value.trim();
      var optIn = root.querySelector('#ef-optin').checked;
      state.email = { address: address, optIn: optIn, sent: false };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) { state.error = 'Enter an email address like name@example.com.'; rerenderInPlace('ef-email'); return; }
      state.busy = true; state.error = null; rerenderInPlace();
      sendEmail(address, optIn).then(function () {
        state.busy = false; state.email.sent = true;
        track('email', { opt_in: optIn ? 'yes' : 'no', size: state.result.sizeKey });
        rerenderInPlace();
      }).catch(function () {
        state.busy = false; state.error = 'That did not send. Check the address and try again.';
        rerenderInPlace('ef-email');
      });
    });

    // Whole accessory tile toggles its tick. Kalles' hand.min.js cancels the click a <label>
    // forwards to its checkbox after a touch, so on phones only the tiny box worked. Toggle in
    // JS instead; preventDefault stops the label's own activation so desktop doesn't double-toggle.
    // The checkbox itself (keyboard + direct taps), links and the quantity buttons keep their own behaviour.
    root.addEventListener('click', function (e) {
      var tile = e.target.closest && e.target.closest('.ef-acc');
      if (!tile || e.target.closest('input, a, button, .ef-qty')) return;
      var box = tile.querySelector('input[type=checkbox]');
      if (!box) return;
      e.preventDefault();
      box.checked = !box.checked;
      box.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // "View details" links open product pages in a new tab; worth knowing what people check.
    root.addEventListener('click', function (e) {
      var d = e.target.closest && e.target.closest('[data-ef-details]');
      if (d) track('details_click', { handle: d.getAttribute('data-ef-details'), place: d.classList.contains('ef-acc-link') ? 'accessory' : 'enclosure', ef_step: state.step });
    });

    // Back from the cart: the browser's back/forward cache restores this page exactly as it
    // left, mid-"Adding...", with every button disabled. Unfreeze it (picks stay ticked).
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted || !state.busy) return;
      state.busy = false; state.error = null;
      render();
      track('return_from_cart', { ef_step: state.step });
    });

    // ---------- boot ----------
    var fromLink = readUrl();
    track('journey_start', { entry: fromLink ? 'result_link' : 'fresh' });
    if (fromLink) { submit(true); } else { render(); trackView(); }

    return { state: function () { return state; }, products: PRODUCTS };
  }

  function boot() {
    var roots = document.querySelectorAll('[data-enclosure-finder]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  window.EnclosureFinderUI = { init: init, boot: boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
